import { StorageKeyType } from '@/core/data/storage/storage.interface';
import type { StorageRepository } from '@/core/data/storage/storage.repository';

const MAX_INFORMED_SPARK_SUCCESS_GAMBITS_PER_USER = 200;

export enum SuppressableDialog {
  GAMBIT_ORGANIZER_LEFT = 'gambitOrganizerLeft',
  ENABLE_PUSH_NOTIFICATIONS = 'enablePushNotifications',
  NOTIFICATIONS_PROMPT_SKIPPED = 'notificationsPromptSkipped',
  LOCATION_CREATE_SUCCESS = 'locationCreateSuccess',
  GAMBIT_ADD_STREAMER_COACH = 'gambitAddStreamerCoach',
  HANDSHAKE_BUTTON_CLICKED = 'handshakeButtonClicked',
}

export interface DialogSuppressData {
  [SuppressableDialog.GAMBIT_ORGANIZER_LEFT]?: boolean;
  [SuppressableDialog.ENABLE_PUSH_NOTIFICATIONS]?: boolean;
  [SuppressableDialog.LOCATION_CREATE_SUCCESS]?: boolean;
  [SuppressableDialog.GAMBIT_ADD_STREAMER_COACH]?: boolean;
  [SuppressableDialog.HANDSHAKE_BUTTON_CLICKED]?: boolean;
  [SuppressableDialog.NOTIFICATIONS_PROMPT_SKIPPED]?: boolean;
  informedSparkSuccessGambitsByUser?: Record<string, string[]>;
}

export class DialogRepository {
  private readonly storage: StorageRepository;

  constructor(storage: StorageRepository) {
    this.storage = storage;
  }

  getDialogSettings(): DialogSuppressData {
    const suppressDialogs = this.storage.get<DialogSuppressData>(StorageKeyType.SupressDialogs);
    return (
      suppressDialogs || {
        [SuppressableDialog.GAMBIT_ORGANIZER_LEFT]: false,
        [SuppressableDialog.ENABLE_PUSH_NOTIFICATIONS]: false,
        [SuppressableDialog.LOCATION_CREATE_SUCCESS]: false,
        [SuppressableDialog.GAMBIT_ADD_STREAMER_COACH]: false,
        [SuppressableDialog.HANDSHAKE_BUTTON_CLICKED]: false,
        [SuppressableDialog.NOTIFICATIONS_PROMPT_SKIPPED]: false,
      }
    );
  }

  getDialogSetting(dialog: SuppressableDialog): boolean {
    const suppressDialogs = this.getDialogSettings();
    return suppressDialogs[dialog] || false;
  }

  suppressDialog(dialog: SuppressableDialog) {
    const suppressDialogs = this.getDialogSettings();
    suppressDialogs[dialog] = true;
    this.storage.set(StorageKeyType.SupressDialogs, suppressDialogs);
  }

  getInformedSparkSuccessGambitIds(userId: string): string[] {
    if (!userId) {
      return [];
    }

    const suppressDialogs = this.getDialogSettings();
    const informedByUser = suppressDialogs.informedSparkSuccessGambitsByUser;

    if (!informedByUser || typeof informedByUser !== 'object') {
      return [];
    }

    const informedIds = informedByUser[userId];

    if (!Array.isArray(informedIds)) {
      return [];
    }

    return this.normalizeGambitIds(informedIds);
  }

  hasInformedSparkSuccessGambit(userId: string, gambitId: string): boolean {
    if (!userId || !gambitId) {
      return false;
    }

    const informedIds = this.getInformedSparkSuccessGambitIds(userId);
    return informedIds.includes(gambitId);
  }

  markInformedSparkSuccessGambit(userId: string, gambitId: string): void {
    if (!userId || !gambitId) {
      return;
    }

    const suppressDialogs = this.getDialogSettings();
    const informedByUser =
      suppressDialogs.informedSparkSuccessGambitsByUser &&
      typeof suppressDialogs.informedSparkSuccessGambitsByUser === 'object'
        ? suppressDialogs.informedSparkSuccessGambitsByUser
        : {};
    const currentInformedIds = Array.isArray(informedByUser[userId]) ? informedByUser[userId] : [];
    const normalizedInformedIds = this.normalizeGambitIds([...currentInformedIds, gambitId]);

    suppressDialogs.informedSparkSuccessGambitsByUser = {
      ...informedByUser,
      [userId]: normalizedInformedIds,
    };

    this.storage.set(StorageKeyType.SupressDialogs, suppressDialogs);
  }

  private normalizeGambitIds(ids: unknown[]): string[] {
    const sanitizedIds = ids.filter((id): id is string => typeof id === 'string' && id.length > 0);
    const dedupedIds = [...new Set(sanitizedIds)];

    if (dedupedIds.length <= MAX_INFORMED_SPARK_SUCCESS_GAMBITS_PER_USER) {
      return dedupedIds;
    }

    return dedupedIds.slice(-MAX_INFORMED_SPARK_SUCCESS_GAMBITS_PER_USER);
  }
}
